import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { Button } from '@/components/ui/button';
import { useDismissedBanner } from '@/hooks/useDismissedBanner';
import {
  getPushStatus,
  isPushSupported,
  registerPush,
  type PushStatus,
} from '@/lib/pushNotifications';

// CSC-295 — one-time opt-in banner for OS-level Web Push notifications.
// Renders ONLY for permission-holding users who:
//   • haven't dismissed it before (localStorage flag), AND
//   • haven't already opted in (permission still 'default'), AND
//   • are on a browser that supports Web Push.
// Never auto-prompts — modern browsers permanently block origins that prompt
// without a user gesture, so the explicit "Enable" click IS the gesture.
const DISMISS_KEY = 'push-onboarding-rfq';

export function PushOnboardingBanner() {
  const hasReviewInbox = useAuthStore((s) =>
    s.hasPermission(PERMISSIONS.QUOTATIONS_REVIEW_INBOX),
  );
  const { isDismissed, dismiss } = useDismissedBanner(DISMISS_KEY);
  const [status, setStatus] = useState<PushStatus>('off');
  const [isWorking, setIsWorking] = useState(false);

  useEffect(() => {
    if (!hasReviewInbox || isDismissed) return;
    let cancelled = false;
    void getPushStatus().then((next) => {
      if (!cancelled) setStatus(next);
    });
    return () => {
      cancelled = true;
    };
  }, [hasReviewInbox, isDismissed]);

  // Hide for users without the permission, in unsupported browsers, when
  // they've already opted in (or been blocked), or after they've dismissed.
  if (!hasReviewInbox) return null;
  if (isDismissed) return null;
  if (!isPushSupported()) return null;
  if (status === 'on' || status === 'denied' || status === 'unsupported') return null;

  async function handleEnable() {
    setIsWorking(true);
    try {
      const next = await registerPush();
      setStatus(next);
      if (next === 'on') {
        toast.success('Desktop notifications enabled');
        dismiss();
      } else if (next === 'denied') {
        toast.error(
          'Notifications were blocked. Re-enable them in your browser site settings to opt in.',
        );
        dismiss();
      }
    } catch (err) {
      console.error('Push registration failed:', err);
      toast.error('Could not enable notifications');
    } finally {
      setIsWorking(false);
    }
  }

  return (
    <div className="flex items-center gap-3 border-b border-warning/30 bg-warning/10 px-4 py-2 text-sm">
      <Bell className="h-4 w-4 shrink-0 text-warning" />
      <div className="flex-1 min-w-0">
        <span className="font-medium text-warning">Stay on top of new RFQs.</span>{' '}
        <span className="text-warning">
          Get a desktop toast the moment an inbound quotation email lands — even when this tab is closed.
        </span>
      </div>
      <Button
        type="button"
        size="sm"
        onClick={handleEnable}
        disabled={isWorking}
        className="h-7 shrink-0 bg-warning text-warning-foreground hover:bg-warning/90"
      >
        {isWorking ? 'Enabling…' : 'Enable'}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={dismiss}
        className="h-7 w-7 shrink-0 p-0 text-warning hover:bg-warning/10"
        aria-label="Dismiss notification opt-in banner"
        title="Maybe later"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
